import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const ContemporaryADUDesignFremont = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Contemporary ADU Design & Construction in Fremont",
    "description": "Custom Contemporary ADUs in Fremont. Expert design & construction by UDA ADU. Modern contemporary architecture with cutting-edge design.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU"
    },
    "url": "https://www.udaadu.com/contemporary-adu-design-fremont"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/contemporary-adu-design-fremont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] relative">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
            <iframe
              src="https://calendly.com/udaadu/30min"
              width="100%"
              height="100%"
              frameBorder="0"
              className="rounded-lg"
            ></iframe>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Contemporary ADU Design & Construction in Fremont
          </h1>
          <p className="text-xl text-gray-200 max-w-4xl mx-auto mb-8">
            Embrace cutting-edge design with a contemporary ADU in Fremont that represents the pinnacle of modern architecture. Our innovative designs feature bold geometries, sustainable materials, and smart technology integration for the ultimate in contemporary living.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Schedule Design Consultation
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/landing">Try UDA AI Designer</Link>
            </Button>
          </div>
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Contemporary ADU Design Elements
            </h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Clean Geometric Lines</h3>
                  <p className="text-gray-200">Bold, angular forms and minimalist aesthetic principles</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Floor-to-Ceiling Windows</h3>
                  <p className="text-gray-200">Expansive glazing maximizing natural light and views</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Mixed Materials</h3>
                  <p className="text-gray-200">Steel, glass, concrete, and wood in innovative combinations</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Open Floor Plans</h3>
                  <p className="text-gray-200">Seamless indoor-outdoor living with flexible spaces</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-[#FF7A00] rounded-full mt-2"></div>
                <div>
                  <h3 className="font-semibold text-white">Smart Technology</h3>
                  <p className="text-gray-200">Integrated home automation and energy management systems</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl font-semibold mb-6 text-white">
              Contemporary ADU Benefits in Fremont
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Future-Forward Design</h3>
                <p className="text-gray-200">Cutting-edge architecture that won't become outdated</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Energy Efficiency</h3>
                <p className="text-gray-200">Advanced building systems minimize environmental impact</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Maximum Space</h3>
                <p className="text-gray-200">Efficient layouts make every square foot count</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <h3 className="font-semibold text-white mb-2">Tech Integration</h3>
                <p className="text-gray-200">Built-in infrastructure for modern living needs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sustainability Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Sustainable Contemporary Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Solar Integration</h3>
              <p className="text-gray-200">Built-in solar panels and battery storage systems</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Green Roofs</h3>
              <p className="text-gray-200">Living roofs that provide insulation and manage stormwater</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Passive Design</h3>
              <p className="text-gray-200">Natural ventilation and daylight harvesting strategies</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Recycled Materials</h3>
              <p className="text-gray-200">Sustainable and reclaimed materials in modern applications</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Water Conservation</h3>
              <p className="text-gray-200">Rainwater collection and greywater recycling systems</p>
            </div>
            <div className="text-center p-6 bg-muted rounded-lg">
              <h3 className="font-semibold mb-4 text-white">Smart Climate Control</h3>
              <p className="text-gray-200">Automated HVAC systems optimizing energy use</p>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-center mb-12 text-white">
            Contemporary ADU Design Process in Fremont
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Vision Development</h3>
              <p className="text-gray-200">Explore contemporary design possibilities for your Fremont site</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Innovative Design</h3>
              <p className="text-gray-200">Create cutting-edge plans integrating latest technologies</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Advanced Approvals</h3>
              <p className="text-gray-200">Navigate Fremont's design review for innovative architecture</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF7A00] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <h3 className="font-semibold mb-2 text-white">Precision Build</h3>
              <p className="text-gray-200">Expert construction using advanced materials and methods</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-3xl font-semibold mb-4 text-white">
            Create Your Contemporary Masterpiece in Fremont
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Design a contemporary ADU that represents the future of sustainable, intelligent living. Our Fremont specialists will create an architectural statement that pushes boundaries while enhancing your lifestyle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={openCalendlyModal} size="lg" className="text-lg px-8 py-3">
              Start Contemporary Project
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-3">
              <Link to="/adu-calculator">Get Contemporary Quote</Link>
            </Button>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4 text-white">Explore More Fremont ADU Styles</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/modern-farmhouse-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Modern Farmhouse ADUs Fremont
            </Link>
            <Link to="/craftsman-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Craftsman ADUs Fremont
            </Link>
            <Link to="/transitional-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Transitional ADUs Fremont
            </Link>
            <Link to="/spanish-colonial-adu-design-fremont" className="text-[#FF7A00] hover:underline">
              Spanish Colonial ADUs Fremont
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default ContemporaryADUDesignFremont;